import { useQueryClient } from '@tanstack/react-query';
import {
  AlertCircle,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  DownloadCloud,
  ExternalLink,
  GitBranch,
  History,
  Loader2,
  UploadCloud,
} from 'lucide-react';
import { useEffect, type ReactNode, useState } from 'react';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { MarkdownText } from '@/components/chat/MarkdownText';
import { useIsInDraftMode } from '@/hooks/useDraftMode';
import {
  useDevelopCommits,
  useDevelopInfo,
  useGitJobStatus,
  usePullDevelop,
  usePushDevelop,
} from '@/hooks/useGit';
import { TERMINAL_JOB_STATUSES, type DevelopCommit } from '@/services/git.service';
import { toastService } from '@/services/toast.service';

type GitOp = 'push' | 'pull';

const OP_COPY: Record<
  GitOp,
  {
    confirmTitle: string;
    confirmBody: (repo: string | null) => ReactNode;
    running: string;
    runningBody: string;
    successTitle: string;
    successBody: string;
    successToast: string;
    successToastBody: string;
  }
> = {
  push: {
    confirmTitle: 'Push Develop to git?',
    confirmBody: (repo) => (
      <>
        This exports the current Develop models and pushes them onto the{' '}
        <span className="font-mono">develop</span> branch of {repo}. Existing commits are preserved.
      </>
    ),
    running: 'Pushing Develop to git…',
    runningBody: 'Exporting Develop and pushing to the develop branch…',
    successTitle: 'Pushed to git',
    successBody:
      "Pull 'pliable/develop' locally and rebase your branch onto it, then Sync to Develop again.",
    successToast: 'Develop pushed to git',
    successToastBody: 'Pull the develop branch locally and rebase onto it to sync.',
  },
  pull: {
    confirmTitle: 'Pull git into Develop?',
    confirmBody: (repo) => (
      <>
        This re-imports the <span className="font-mono">develop</span> branch of {repo} into Develop
        — the same import the push webhook runs. Use it to recover from a missed or failed webhook.
      </>
    ),
    running: 'Importing git into Develop…',
    runningBody: 'Cloning the develop branch and importing its models into Develop…',
    successTitle: 'Imported into Develop',
    successBody: 'Develop now reflects the develop branch.',
    successToast: 'Git imported into Develop',
    successToastBody: 'Develop now reflects the develop branch.',
  },
};

/**
 * Git only ever acts on Develop, so in production mode there is nothing to show
 * and nothing worth fetching. The gate lives in front of GitSettings rather than
 * inside it so none of its queries mount.
 */
export default function GitTab() {
  const isInDraftMode = useIsInDraftMode();

  if (!isInDraftMode) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <GitBranch className="h-4 w-4" /> Develop branch
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">Git is only available in Develop mode.</p>
        </CardContent>
      </Card>
    );
  }

  return <GitSettings />;
}

function GitSettings() {
  const queryClient = useQueryClient();
  const { data: develop, isLoading } = useDevelopInfo();
  const pushMutation = usePushDevelop();
  const pullMutation = usePullDevelop();
  const [confirmOp, setConfirmOp] = useState<GitOp | null>(null);
  const [activeOp, setActiveOp] = useState<GitOp | null>(null);
  const [jobId, setJobId] = useState<string | null>(null);
  const { data: jobStatus } = useGitJobStatus(jobId);

  const status = jobStatus?.job_status;
  const isTerminal = !!status && TERMINAL_JOB_STATUSES.has(status);
  const isRunning = !!jobId && !isTerminal;
  const succeeded = status === 'finished';
  const repo = develop?.repo ?? null;
  const copy = activeOp ? OP_COPY[activeOp] : null;
  const inSync = develop?.in_sync === true;

  useEffect(() => {
    if (!jobId || !isTerminal || !copy) return;
    // Refresh the displayed SHAs / sync status once the job lands.
    queryClient.invalidateQueries({ queryKey: ['git-develop'] });
    if (succeeded) {
      toastService.success(copy.successToast, { description: copy.successToastBody });
    } else {
      toastService.error('Job failed', {
        description: jobStatus?.error_message || 'The git job failed.',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId, isTerminal]);

  const start = async (op: GitOp) => {
    setConfirmOp(null);
    setActiveOp(op);
    try {
      const mutation = op === 'push' ? pushMutation : pullMutation;
      const res = await mutation.mutateAsync();
      setJobId(res.job_id);
    } catch (e) {
      setActiveOp(null);
      toastService.error('Could not start job', {
        description: e instanceof Error ? e.message : String(e),
      });
    }
  };

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <GitBranch className="h-4 w-4" /> Develop branch
          </CardTitle>
          <CardDescription>
            Sync the platform&apos;s Develop models with git. Push writes Develop onto the develop
            branch; pull re-imports the develop branch into Develop.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {isLoading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading…
            </div>
          ) : !repo ? (
            <p className="text-sm text-muted-foreground">
              No Pliable git repo is provisioned for this tenant yet.
            </p>
          ) : (
            <>
              <Row label="Repository" value={<span className="font-mono text-sm">{repo}</span>} />
              <Row
                label="Branch"
                value={
                  <a
                    href={develop?.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-primary hover:underline"
                  >
                    {develop?.branch ?? 'develop'} <ExternalLink className="h-3 w-3" />
                  </a>
                }
              />
              <Row
                label="Latest commit"
                value={
                  <span className="font-mono text-sm">
                    {develop?.sha ? develop.sha.slice(0, 7) : '—'}
                  </span>
                }
              />
              <Row
                label="Develop status"
                value={
                  inSync ? (
                    <Badge variant="outline" className="gap-1 border-green-600/40 text-green-700">
                      <CheckCircle2 className="h-3 w-3" /> In sync
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="gap-1 border-amber-600/40 text-amber-700">
                      <AlertCircle className="h-3 w-3" /> Out of sync
                    </Badge>
                  )
                }
              />
              {!inSync && (
                <p className="text-xs text-muted-foreground">
                  The develop branch has commits Develop hasn&apos;t imported. Pull git into Develop to
                  catch up (e.g. after a missed webhook).
                </p>
              )}
              <div className="flex gap-2 pt-2">
                <Button onClick={() => setConfirmOp('push')} disabled={isRunning}>
                  <UploadCloud className="h-4 w-4 mr-2" /> Push Develop to Git
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setConfirmOp('pull')}
                  disabled={isRunning}
                >
                  <DownloadCloud className="h-4 w-4 mr-2" /> Pull Git Into Develop
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {repo && <DevelopCommitsCard />}

      <AlertDialog open={!!confirmOp} onOpenChange={(open) => !open && setConfirmOp(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{confirmOp && OP_COPY[confirmOp].confirmTitle}</AlertDialogTitle>
            <AlertDialogDescription>
              {confirmOp && OP_COPY[confirmOp].confirmBody(repo)}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <Button variant="outline" onClick={() => setConfirmOp(null)}>
              Cancel
            </Button>
            <Button onClick={() => confirmOp && void start(confirmOp)}>
              {confirmOp === 'pull' ? 'Pull' : 'Push'}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog
        open={!!jobId}
        onOpenChange={(open) => {
          if (!open && !isRunning) {
            setJobId(null);
            setActiveOp(null);
          }
        }}
      >
        <DialogContent
          onPointerDownOutside={(e) => {
            if (isRunning) e.preventDefault();
          }}
          onEscapeKeyDown={(e) => {
            if (isRunning) e.preventDefault();
          }}
        >
          <DialogHeader>
            <DialogTitle>
              {isRunning
                ? copy?.running
                : succeeded
                  ? copy?.successTitle
                  : 'Job failed'}
            </DialogTitle>
            <DialogDescription>
              {isRunning
                ? copy?.runningBody
                : succeeded
                  ? copy?.successBody
                  : jobStatus?.error_message || 'The git job failed.'}
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-center justify-center py-6">
            {isRunning ? (
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            ) : succeeded ? (
              <CheckCircle2 className="h-8 w-8 text-green-600" />
            ) : (
              <AlertCircle className="text-destructive h-8 w-8" />
            )}
          </div>
          <DialogFooter>
            <Button
              onClick={() => {
                setJobId(null);
                setActiveOp(null);
              }}
              disabled={isRunning}
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function Row({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-4">
      <span className="text-sm text-muted-foreground">{label}</span>
      {value}
    </div>
  );
}

function DevelopCommitsCard() {
  const [page, setPage] = useState(1);
  const { data, isLoading, isFetching } = useDevelopCommits(page);
  const commits = data?.commits ?? [];

  return (
    <Card className="mt-4">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <History className="h-4 w-4" /> Recent syncs to Develop
        </CardTitle>
        <CardDescription>
          Commits on the develop branch, newest first — the message is the summary of what
          changed and why, written when the work was synced.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {isLoading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : commits.length === 0 ? (
          <p className="text-sm text-muted-foreground">No commits on the develop branch yet.</p>
        ) : (
          <>
            <ul className="divide-y divide-border">
              {commits.map((c) => (
                <CommitRow key={c.sha} commit={c} />
              ))}
            </ul>
            <div className="flex items-center justify-between pt-1">
              <Button
                variant="outline"
                size="sm"
                disabled={page <= 1 || isFetching}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Newer
              </Button>
              <span className="text-xs text-muted-foreground">Page {page}</span>
              <Button
                variant="outline"
                size="sm"
                disabled={!data?.has_more || isFetching}
                onClick={() => setPage((p) => p + 1)}
              >
                Older
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

function CommitRow({ commit }: { commit: DevelopCommit }) {
  const [expanded, setExpanded] = useState(false);
  const lines = commit.message.split('\n');
  const subject = lines[0] || '(no message)';
  const hasBody = lines.slice(1).some((l) => l.trim().length > 0);

  return (
    <li className="py-2">
      <div className="flex items-start gap-2">
        <button
          type="button"
          onClick={() => hasBody && setExpanded((v) => !v)}
          className="mt-0.5 text-muted-foreground hover:text-foreground disabled:opacity-0"
          disabled={!hasBody}
          aria-label={expanded ? 'Collapse' : 'Expand'}
        >
          {expanded ? (
            <ChevronDown className="h-4 w-4" />
          ) : (
            <ChevronRight className="h-4 w-4" />
          )}
        </button>
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline justify-between gap-3">
            <span className="truncate text-sm font-medium">{subject}</span>
            <a
              href={commit.url ?? undefined}
              target="_blank"
              rel="noopener noreferrer"
              className="shrink-0 font-mono text-xs text-primary hover:underline"
            >
              {commit.short_sha}
            </a>
          </div>
          <div className="text-xs text-muted-foreground">
            {commit.author ?? 'unknown'}
            {commit.date ? ` · ${new Date(commit.date).toLocaleString()}` : ''}
          </div>
          {expanded && hasBody && (
            <div className="mt-2 rounded-md border border-border bg-muted/30 p-3">
              <MarkdownText content={commit.message} />
            </div>
          )}
        </div>
      </div>
    </li>
  );
}
